import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AccessManagementDesignation,
  AccessManagementDesignationRecord,
  AccessManagementGroupedDesignation,
  AccessManagementGroupedDesignationRecord,
} from '@cdw-ae/common-data';
import {
  ApiResponse,
  ICdwSearchGridDataService,
} from '@cdw-ae/common-ui-components-search-grid';
import {
  IGetSqlDataParams,
  SelectedFilterField,
  SqlResponse,
} from '@cdw-ae/filter-bar-interfaces';
import { DESIGNATIONS_ROUTES } from '@cdw-ae/routes-access-api';
import { getQueryStringFromParams } from '@cdw-ae/routes-shared-api';
import { catchError, map, Observable, of } from 'rxjs';
import { ApiUrlBuilderService } from '../../../../../../common/services/api-url-builder.service';
import { AccessManagementFrontendAuthService } from '../access-management-frontend-auth.service';

export const httpOptions: { headers: HttpHeaders } = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'x-cdw-app-name': 'Gopher for Chrome WebApp',
  }),
};

@Injectable({
  providedIn: 'root',
})
export class DesignationsDataService
  implements ICdwSearchGridDataService<AccessManagementGroupedDesignationRecord>
{
  constructor(
    private httpClient: HttpClient,
    _accessManagementAuthService: AccessManagementFrontendAuthService,
    private readonly urlBuilder: ApiUrlBuilderService
  ) {}

  public fetchSqlData(
    params: IGetSqlDataParams
  ): Observable<SqlResponse<AccessManagementGroupedDesignationRecord>> {
    const { activeFilters, startRow, endRow }: IGetSqlDataParams = params;
    return this.getAccessManagementGroupedDesignations(
      activeFilters,
      startRow,
      endRow
    );
  }

  public getDesignations(
    activeFilters?: SelectedFilterField[],
    startRow?: number,
    endRow?: number
  ): Observable<SqlResponse<AccessManagementDesignationRecord>> {
    const url = this.urlBuilder.backendApi('designations', DESIGNATIONS_ROUTES.ROOT);
    const queryString = getQueryStringFromParams({
      activeFilters,
      startRow,
      endRow,
    });
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    return this.httpClient.get<SqlResponse<AccessManagementDesignationRecord>>(
      fullUrl
    ).pipe(
      catchError(() => of({ data: [], itemTotal: 0 } as SqlResponse<AccessManagementDesignationRecord>))
    );
  }

  public getDesignationById(
    designationId: number
  ): Observable<{
    success: boolean;
    data: AccessManagementDesignationRecord;
  }> {
    const url = this.urlBuilder.backendApi('designations', DESIGNATIONS_ROUTES.GET_BY_ID, { id: designationId });
    return this.httpClient
      .get<AccessManagementDesignationRecord>(
        url,
        httpOptions
      )
      .pipe(
        map((res) => ({
          success: true,
          data: res,
        })),
        catchError(() => of({ success: false, data: null as unknown as AccessManagementDesignationRecord }))
      );
  }

  public updateDesignation(
    // Matches addDesignation's shape: caller sends `roleIds` (a list) so the
    // backend can rebind the existing record to roleIds[0] AND insert
    // additional designation rows for the rest in a single transaction.
    // Optional metadata-only updates can omit roleIds entirely.
    newDesignation: Omit<AccessManagementDesignation, 'customerId' | 'roleId'> & {
      roleIds?: number[];
    },
    designationId: number
  ): Observable<ApiResponse<AccessManagementDesignationRecord>> {
    const url = this.urlBuilder.backendApi('designations', DESIGNATIONS_ROUTES.GET_BY_ID, { id: designationId });
    return this.httpClient
      .put<AccessManagementDesignationRecord>(
        url,
        newDesignation,
        httpOptions
      )
      .pipe(
        map(
          (res): ApiResponse<AccessManagementDesignationRecord> => ({
            success: true,
            data: [res],
          })
        ),
        catchError(() => of({ success: false, data: [] } as ApiResponse<AccessManagementDesignationRecord>))
      );
  }

  public deleteDesignation(
    designationId: number
  ): Promise<Observable<{ success: boolean }>> {
    const url = this.urlBuilder.backendApi('designations', DESIGNATIONS_ROUTES.GET_BY_ID, { id: designationId });
    return Promise.resolve(
      this.httpClient.delete<{ success: boolean }>(url).pipe(
        catchError(() => of({ success: false }))
      )
    );
  }

  public addDesignation(
    newDesignation: Omit<AccessManagementDesignation, 'customerId' | 'roleId'> & { roleIds: number[] }
  ): Observable<ApiResponse<AccessManagementDesignationRecord>> {
    const url = this.urlBuilder.backendApi('designations', DESIGNATIONS_ROUTES.ROOT);
    return this.httpClient
      .post(url, newDesignation, httpOptions)
      .pipe(
        map((res) => ({
          success: true,
          data: res as AccessManagementDesignationRecord[],
        })),
        catchError(() => of({ success: false, data: [] as AccessManagementDesignationRecord[] }))
      );
  }

  public getAccessManagementGroupedDesignations(
    activeFilters?: SelectedFilterField[],
    startRow?: number,
    endRow?: number
  ): Observable<SqlResponse<AccessManagementGroupedDesignation>> {
    const url = this.urlBuilder.backendApi('designations', DESIGNATIONS_ROUTES.GROUPED);
    const queryString = getQueryStringFromParams({
      ...(activeFilters !== undefined && { activeFilters }),
      ...(startRow !== undefined && { startRow }),
      ...(endRow !== undefined && { endRow }),
    });
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    return this.httpClient.get<
      SqlResponse<AccessManagementGroupedDesignation>
    >(fullUrl).pipe(
      catchError(() => of({ data: [], itemTotal: 0 } as SqlResponse<AccessManagementGroupedDesignation>))
    );
  }
}
